import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'

declare global {
  interface Window {
    $crisp: any[]
    CRISP_WEBSITE_ID: string
  }
}

@Injectable({
  providedIn: 'root',
})
export class CrispService {
  private readonly CRISP_ID = 'f8fac3a9-ce9e-4860-bceb-e07fdaa2bde3'

  constructor(private http: HttpClient) {}

  initCrisp(currentUser: any): void {
    if (!window.$crisp) {
      window.$crisp = []
    }

    this.pushToCrisp(['safe', true])

    if (!window.CRISP_WEBSITE_ID) {
      window.CRISP_WEBSITE_ID = this.CRISP_ID
    }

    const existingScript = document.getElementById('crisp-script')

    if (!existingScript) {
      const script = document.createElement('script')
      script.id = 'crisp-script'
      script.src = 'https://client.crisp.chat/l.js'
      script.async = true
      document.head.appendChild(script)

      script.onload = () => this.setUserData(currentUser)
    } else {
      this.setUserData(currentUser)
    }
  }

  private setUserData(user: any): void {
    this.pushToCrisp(['safe', true])

    window.$crisp.push([
      'on',
      'session:loaded',
      () => {
        if (user?.email) {
          this.pushToCrisp(['set', 'user:email', [user.email]])
        }

        if (user?.name) {
          const fullName = user.surname ? `${user.name} ${user.surname}` : user.name
          this.pushToCrisp(['set', 'user:name', [fullName]])
          this.pushToCrisp(['set', 'user:nickname', [user.name]])
        }

        if (user?.phone) {
          this.pushToCrisp(['set', 'user:phone', [user.phone]])
        }

        if (user?.team?.clientName) {
          this.pushToCrisp(['set', 'user:company', [user.team.clientName]])
        }

        const clientId = user?.team?.clientId
        const linxioUrl = clientId ? `https://app.linxio.com/admin/clients/${clientId}/company-info` : 'https://app.linxio.com/'

        const formattedDate = this.formatDate(user?.team?.companyCreateDate?.date)

        const sessionData = this.toScalarPairs([
          ['user_id', user?.id],
          ['linxio_url', linxioUrl],
          ['company_plan', user?.plan?.displayName],
          ['company_create_date', formattedDate],
          ['company_devices_total', user?.team?.companyDevicesTotal],
          ['company_vehicles_total', user?.team?.companyVehiclesTotal],
          ['company_users_total', user?.team?.companyUsersTotal],
          ['device_types', user?.team?.deviceTypes],
        ])

        if (sessionData.length) {
          this.pushToCrisp(['set', 'session:data', [sessionData]])
        }
      },
    ])
  }

  private formatDate(rawDate?: string): string | undefined {
    if (!rawDate) return undefined
    const parsed = new Date(rawDate)
    if (isNaN(parsed.getTime())) return rawDate
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(parsed)
  }

  // Crisp validates session:data as a whole — a single undefined/null/array value makes it reject
  // every pair with `throw new Error('Invalid data')`. Arrays are joined; non-scalars are dropped.
  private toScalarPairs(pairs: [string, unknown][]): [string, string | number | boolean][] {
    return pairs
      .map(([key, value]): [string, unknown] => [key, Array.isArray(value) ? value.join(', ') : value])
      .filter((pair): pair is [string, string | number | boolean] => {
        const value = pair[1]
        return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      })
  }

  private pushToCrisp(command: any[]): void {
    window.$crisp.push(command)
  }
}
